import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { 
  Menu, 
  X, 
  ArrowRight, 
  Globe, 
  Palette, 
  RefreshCw, 
  Server,
  Crown,
  Phone,
  Mail,
  MapPin,
  Twitter,
  Linkedin,
  Instagram,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "sonner";
import TermsPage from "@/pages/TermsPage";
import PricingPage from "@/pages/PricingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookieConsent from "@/components/CookieConsent";
import IntroOverlay from "@/components/IntroOverlay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Header Component
const Header = ({ scrollToSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", id: "services" },
    { name: "Pricing", id: "pricing", isPage: true },
    { name: "Portfolio", id: "portfolio" },
    { name: "Contact", id: "contact" }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-4" : "py-6"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <motion.a 
          href="#" 
          className="font-['Oswald'] text-xl md:text-2xl font-bold tracking-tight"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          data-testid="logo"
        >
          WILTSHIRE<span className="text-[#FF4500]">WEB</span><span className="text-[#888888] font-normal ml-1">STUDIO</span>
        </motion.a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isPage ? (
              <Link
                key={link.id}
                to={`/${link.id}`}
                className="nav-link font-['Manrope'] text-sm uppercase tracking-widest text-[#888888] hover:text-white transition-colors"
                data-testid={`nav-${link.id}`}
              >
                {link.name}
              </Link>
            ) : (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="nav-link font-['Manrope'] text-sm uppercase tracking-widest text-[#888888] hover:text-white transition-colors"
                data-testid={`nav-${link.id}`}
              >
                {link.name}
              </button>
            )
          ))}
          <Button 
            onClick={() => scrollToSection("contact")}
            className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider"
            data-testid="nav-cta-button"
          >
            Get Started
          </Button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button 
          className="block md:hidden text-white p-2"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mobile-menu-overlay fixed inset-0 top-16 md:hidden flex flex-col items-center justify-center gap-8"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              link.isPage ? (
                <Link
                  key={link.id}
                  to={`/${link.id}`}
                  onClick={() => setIsOpen(false)}
                  className="font-['Oswald'] text-3xl uppercase tracking-wider text-white hover:text-[#FF4500] transition-colors"
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.name}
                </Link>
              ) : (
                <button
                  key={link.id}
                  onClick={() => {
                    scrollToSection(link.id);
                    setIsOpen(false);
                  }}
                  className="font-['Oswald'] text-3xl uppercase tracking-wider text-white hover:text-[#FF4500] transition-colors"
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.name}
                </button>
              )
            ))}
            <Button 
              onClick={() => {
                scrollToSection("contact");
                setIsOpen(false);
              }}
              className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider mt-4"
              data-testid="mobile-cta-button"
            >
              Get Started
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero headline cycle — loops through brand hooks
const HERO_LINES = [
  { line1: "NO WEBSITE?", line2: "NO PROBLEM." },
  { line1: "READY IN 48 HOURS.", line2: "FROM £400." },
];

const HeroHeadlineCycle = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let intervalId;
    let firstTickTimeout;
    // Wait for intro overlay (~11.3s with pre-roll) to finish so the first
    // visible headline is always "NO WEBSITE? NO PROBLEM.". Then a short pause,
    // then start cycling at the comfortable 3.5s reading pace.
    const startTimeout = setTimeout(() => {
      firstTickTimeout = setTimeout(() => {
        setIdx((i) => (i + 1) % HERO_LINES.length);
        intervalId = setInterval(() => {
          setIdx((i) => (i + 1) % HERO_LINES.length);
        }, 2800);
      }, 800);
    }, 11500);
    return () => {
      clearTimeout(startTimeout);
      if (firstTickTimeout) clearTimeout(firstTickTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const current = HERO_LINES[idx];

  return (
    <div
      className="relative"
      style={{ minHeight: "clamp(190px, 22vw, 290px)" }}
      data-testid="hero-headline"
    >
      <div className="absolute inset-0 flex justify-start">
        <AnimatePresence mode="wait">
          <motion.h1
            key={idx}
            initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -28, filter: "blur(8px)" }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="heading-display text-white md:whitespace-nowrap"
          >
            {current.line1}
            <br />
            <span className="text-[#FF4500]">{current.line2}</span>
          </motion.h1>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = ({ scrollToSection }) => {
  return (
    <section 
      className="hero-gradient min-h-screen flex items-center relative overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background elements */}
      <div className="absolute inset-0 orange-glow" />
      <div className="absolute bottom-0 left-0 right-0 h-32 gradient-overlay" />
      
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24 relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          <motion.p 
            variants={fadeInUp}
            className="font-['Manrope'] text-[#FF4500] uppercase tracking-[0.3em] text-sm mb-6"
          >
            Wiltshire Web Studio
          </motion.p>
          
          <motion.div variants={fadeInUp} className="mb-6">
            <HeroHeadlineCycle />
          </motion.div>
          
          <motion.p 
            variants={fadeInUp}
            className="font-['Manrope'] text-xl md:text-2xl text-[#888888] max-w-xl mb-6 leading-relaxed"
            data-testid="hero-subheadline"
          >
            I build bold, modern websites for businesses ready to be seen.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex items-center gap-3 mb-12"
          >
            <div className="w-2 h-2 bg-[#FF4500] rounded-full animate-pulse" />
            <p className="font-['Manrope'] text-[#FF4500] font-semibold">
              First draft delivered in as little as 48 hours
            </p>
          </motion.div>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button 
              onClick={() => scrollToSection("contact")}
              className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider text-lg px-8 py-6 group"
              data-testid="hero-primary-cta"
            >
              Get My Website
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button 
              onClick={() => scrollToSection("portfolio")}
              className="btn-secondary rounded-none font-['Oswald'] uppercase tracking-wider text-lg px-8 py-6"
              data-testid="hero-secondary-cta"
            >
              See My Work
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-[#888888] rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#FF4500] rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Problem Section (Inverted)
const ProblemSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section 
      ref={ref}
      className="inverted-section section-padding relative overflow-hidden"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h2 
            className="heading-xl text-[#050505] mb-8"
            data-testid="problem-headline"
          >
            IF YOU'RE NOT ONLINE,<br />
            <span className="text-[#FF4500]">YOU DON'T EXIST.</span>
          </h2>
          <p className="font-['Manrope'] text-lg md:text-xl text-[#333333] max-w-2xl mx-auto leading-relaxed">
            Your customers search online first. Without a website, you're losing trust, 
            credibility, and revenue to competitors who showed up when you didn't.
          </p>
        </motion.div>
      </div>
      
      {/* Decorative element */}
      <motion.div 
        style={{ y }}
        className="absolute -right-32 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#FF4500] opacity-10 blur-3xl"
      />
    </section>
  );
};

// Services Section
const services = [
  {
    icon: Globe,
    title: "Starter Sites",
    price: "£400",
    description: "One-page powerhouses. Perfect for businesses just getting online. First draft in 24-48 hours."
  },
  {
    icon: Palette,
    title: "Custom Builds",
    price: "£700",
    description: "3-5 page websites with blog, analytics and more. Tailored to capture your brand's identity."
  },
  {
    icon: Crown,
    title: "Premium Sites",
    price: "£1,000",
    description: "6-10 page sites with payments, bookings, CRM integration and full training included."
  },
  {
    icon: RefreshCw,
    title: "Redesigns",
    price: "Custom",
    description: "Transform your outdated site into a modern conversion machine."
  },
  {
    icon: Server,
    title: "Hosting & Care",
    price: "From £20/mo",
    description: "Sleep soundly knowing your site is secure, fast, and always online."
  }
];

const ServicesSection = () => {
  return (
    <section 
      id="services"
      className="section-padding bg-[#050505] relative"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <p className="font-['Manrope'] text-[#FF4500] uppercase tracking-[0.3em] text-sm mb-4">
            What I Do
          </p>
          <h2 className="heading-xl text-white mb-4">
            SERVICES
          </h2>
          <p className="font-['Manrope'] text-[#888888] text-lg max-w-2xl mx-auto">
            Everything you need to get your business online and growing
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={fadeInUp}
              className="service-card card-hover bg-[#0A0A0A] border border-[#262626] p-8 flex flex-col"
              data-testid={`service-card-${index}`}
            >
              <div className="flex items-start justify-between mb-6">
                <service.icon className="text-[#FF4500]" size={32} strokeWidth={1.5} />
                <span className="font-['Oswald'] text-[#FF4500] text-lg">{service.price}</span>
              </div>
              <h3 className="font-['Oswald'] text-xl md:text-2xl text-white uppercase mb-3">
                {service.title}
              </h3>
              <p className="font-['Manrope'] text-[#888888] leading-relaxed flex-grow">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <Link to="/pricing">
            <Button className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider px-8 py-6">
              View Full Pricing
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const processSteps = [
  {
    number: "01",
    title: "LEARN",
    subtitle: "I Learn About Your Business",
    description: "I dive deep into understanding your business, your customers, and your goals to create a strategy that works."
  },
  {
    number: "02",
    title: "DESIGN & BUILD",
    subtitle: "I Design & Build",
    description: "I craft a stunning, high-performance website that captures your brand and converts visitors."
  },
  {
    number: "03",
    title: "GO LIVE",
    subtitle: "You Go Live & Get Found",
    description: "I launch your site, optimise for search engines, and watch your business grow online."
  }
];

const ProcessSection = () => {
  return (
    <section 
      id="process"
      className="section-padding bg-[#0A0A0A] relative"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <p className="font-['Manrope'] text-[#FF4500] uppercase tracking-[0.3em] text-sm mb-4">
            How I Work
          </p>
          <h2 className="heading-xl text-white">
            THE PROCESS
          </h2>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#FF4500] to-transparent" />
          
          {processSteps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`flex flex-col md:flex-row items-center gap-8 mb-16 last:mb-0 ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
              data-testid={`process-step-${index}`}
            >
              <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                <span className="font-['Syne'] text-6xl text-[#FF4500] opacity-30">
                  {step.number}
                </span>
                <h3 className="font-['Oswald'] text-3xl md:text-4xl text-white uppercase mb-2">
                  {step.title}
                </h3>
                <p className="font-['Manrope'] text-[#888888] max-w-md mx-auto md:mx-0">
                  {step.description}
                </p>
              </div>
              
              {/* Center dot */}
              <div className="relative">
                <div className="w-4 h-4 rounded-full bg-[#FF4500] animate-pulse-glow" />
              </div>
              
              <div className="flex-1" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Portfolio Section - Website screenshots with links
const portfolioItems = [
  {
    title: "Ridgeway Prints",
    category: "Commercial Printing",
    image: "/portfolio/ridgeway-prints.jpg",
    url: "https://uk-print-pro.preview.emergentagent.com",
    description: "Premium commercial printing company serving UK businesses"
  },
  {
    title: "Kaftan Queens",
    category: "Fashion & E-Commerce",
    image: "/portfolio/kaftan-queens.jpg",
    url: "https://kaftanqueens.co.uk/",
    description: "Beautiful kaftans and resort wear for women — UK-based fashion boutique"
  },
  {
    title: "Leathercraft",
    category: "E-Commerce",
    image: "/portfolio/leathercraft.jpg",
    url: "https://crafted-goods-4.preview.emergentagent.com",
    description: "Premium leather goods curated with 50+ years of expertise"
  },
  {
    title: "Safari Star",
    category: "Business Services",
    image: "/portfolio/safari-star.jpg",
    url: "https://safari-star-preview.preview.emergentagent.com",
    description: "Global partner for company formation & business support across 16 countries"
  },
  {
    title: "VIC Dry Cleaners",
    category: "Local Service",
    image: "/portfolio/vic-dry-cleaners.jpg",
    url: "https://oxford-dry-clean.preview.emergentagent.com",
    description: "Professional garment care & alterations on Cowley Road, Oxford"
  },
  {
    title: "Villa Kephala",
    category: "Luxury Rental",
    image: "/portfolio/villa-kephala.jpg",
    url: "https://villakephala.com",
    description: "Exceptional seafront villa retreat in Kea, Greece"
  },
  {
    title: "The Old Pub",
    category: "Boutique Stay",
    image: "/portfolio/the-old-pub.jpg",
    url: "https://www.theoldpubwiltshire.co.uk/",
    description: "A luxurious private cellar suite with gym & sauna in Chirton, Wiltshire"
  }
];

const PortfolioSection = () => {
  return (
    <section 
      id="portfolio"
      className="section-padding bg-[#050505] relative"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-['Manrope'] text-[#FF4500] uppercase tracking-[0.3em] text-sm mb-4">
            My Work
          </p>
          <h2 className="heading-xl text-white">
            PORTFOLIO
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="portfolio-card group"
              data-testid={`portfolio-item-${index}`}
            >
              <a 
                href={item.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block cursor-pointer"
              >
                <div className="relative overflow-hidden aspect-[4/3] mb-4 bg-[#0A0A0A]">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="portfolio-image w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-4 right-4 bg-[#FF4500] text-white px-3 py-1 text-xs font-['Manrope'] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                    View Site <ArrowRight size={12} />
                  </div>
                </div>
                <p className="font-['Manrope'] text-[#FF4500] uppercase tracking-widest text-xs mb-2">
                  {item.category}
                </p>
                <h3 className="font-['Oswald'] text-xl text-white uppercase group-hover:text-[#FF4500] transition-colors mb-2">
                  {item.title}
                </h3>
                <p className="font-['Manrope'] text-[#888888] text-sm">
                  {item.description}
                </p>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = ({ scrollToSection }) => {
  return (
    <section 
      className="section-padding bg-[#050505] relative overflow-hidden"
      data-testid="final-cta-section"
    >
      <div className="absolute inset-0 orange-glow" />
      
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 
            className="heading-xl text-white mb-8"
            data-testid="final-cta-headline"
          >
            YOUR BUSINESS<br />
            <span className="text-[#FF4500]">DESERVES TO BE SEEN.</span>
          </h2>
          <p className="font-['Manrope'] text-xl text-[#888888] mb-12 max-w-xl mx-auto">
            Stop losing customers to competitors who showed up online. Let's build something powerful together.
          </p>
          <Button 
            onClick={() => scrollToSection("contact")}
            className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider text-lg px-12 py-6 animate-pulse-glow"
            data-testid="final-cta-button"
          >
            Start My Website
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section 
      id="contact"
      className="section-padding contact-form-container relative"
      data-testid="contact-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-['Manrope'] text-[#FF4500] uppercase tracking-[0.3em] text-sm mb-4">
            Get In Touch
          </p>
          <h2 className="heading-xl text-white mb-8">
            LET'S TALK
          </h2>
          <p className="font-['Manrope'] text-[#888888] text-lg mb-12 leading-relaxed max-w-2xl mx-auto">
            Ready to get your business online? Get in touch and I'll get back to you within 24 hours with a free consultation.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-12">
            <a 
              href="mailto:george@wiltshirewebstudio.com"
              className="flex items-center gap-4 group"
              data-testid="contact-email-link"
            >
              <div className="w-14 h-14 border border-[#262626] flex items-center justify-center group-hover:border-[#FF4500] group-hover:bg-[#FF4500] transition-all">
                <Mail size={24} className="text-[#FF4500] group-hover:text-white transition-colors" />
              </div>
              <div className="text-left">
                <p className="font-['Manrope'] text-[#888888] text-sm">Email</p>
                <p className="font-['Manrope'] text-white text-lg group-hover:text-[#FF4500] transition-colors">george@wiltshirewebstudio.com</p>
              </div>
            </a>
            
            <a 
              href="tel:07767071806"
              className="flex items-center gap-4 group"
              data-testid="contact-phone-link"
            >
              <div className="w-14 h-14 border border-[#262626] flex items-center justify-center group-hover:border-[#FF4500] group-hover:bg-[#FF4500] transition-all">
                <Phone size={24} className="text-[#FF4500] group-hover:text-white transition-colors" />
              </div>
              <div className="text-left">
                <p className="font-['Manrope'] text-[#888888] text-sm">Phone</p>
                <p className="font-['Manrope'] text-white text-lg group-hover:text-[#FF4500] transition-colors">07767 071806</p>
              </div>
            </a>
          </div>

          <div className="flex items-center justify-center gap-3 text-[#888888]">
            <MapPin size={16} className="text-[#FF4500]" />
            <p className="font-['Manrope'] text-sm">Based in Wiltshire, United Kingdom</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ scrollToSection }) => {
  const footerLinks = [
    { name: "Services", id: "services" },
    { name: "Process", id: "process" },
    { name: "Portfolio", id: "portfolio" },
    { name: "Contact", id: "contact" }
  ];

  return (
    <footer 
      className="bg-[#050505] border-t border-[#262626] py-16"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="font-['Oswald'] text-3xl font-bold tracking-tight text-white mb-4">
              WILTSHIRE<span className="text-[#FF4500]">WEB</span><span className="text-[#888888] font-normal ml-1">STUDIO</span>
            </h3>
            <p className="font-['Manrope'] text-[#888888] mb-6 max-w-md">
              Website Design for Businesses Without Websites.<br />
              Based in Wiltshire, UK.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 border border-[#262626] flex items-center justify-center hover:border-[#FF4500] hover:bg-[#FF4500] transition-all"
                data-testid="social-twitter"
              >
                <Twitter size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 border border-[#262626] flex items-center justify-center hover:border-[#FF4500] hover:bg-[#FF4500] transition-all"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 border border-[#262626] flex items-center justify-center hover:border-[#FF4500] hover:bg-[#FF4500] transition-all"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Oswald'] text-white uppercase tracking-wider mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="font-['Manrope'] text-[#888888] hover:text-[#FF4500] transition-colors"
                    data-testid={`footer-link-${link.id}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Oswald'] text-white uppercase tracking-wider mb-6">
              Contact
            </h4>
            <ul className="space-y-3 font-['Manrope'] text-[#888888]">
              <li>george@wiltshirewebstudio.com</li>
              <li>07767 071806</li>
              <li>Wiltshire, UK</li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#262626] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-['Manrope'] text-[#888888] text-sm">
            © 2024 Wiltshire Web Studio. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="font-['Manrope'] text-[#888888] text-sm hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="font-['Manrope'] text-[#888888] text-sm hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
function HomePage() {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      {/* Components */}
      <Header scrollToSection={scrollToSection} />
      <HeroSection scrollToSection={scrollToSection} />
      <ProblemSection />
      <ServicesSection />
      <ProcessSection />
      <PortfolioSection />
      <FinalCTASection scrollToSection={scrollToSection} />
      <ContactSection />
      <Footer scrollToSection={scrollToSection} />
    </>
  );
}

// Main App Component
function App() {
  // Remove Emergent badge (but not portfolio links)
  useEffect(() => {
    const removeBadge = () => {
      // Only target specific badge elements, not portfolio links
      document.querySelectorAll('body > div').forEach(el => {
        // Check if it's a fixed position element at bottom right
        const style = window.getComputedStyle(el);
        if (style.position === 'fixed') {
          const text = el.textContent?.toLowerCase() || '';
          if (text.includes('made with') && text.includes('emergent')) {
            el.remove();
          }
        }
      });
      
      // Also check for iframes or scripts from emergent
      document.querySelectorAll('iframe[src*="emergent"], script[src*="emergent"]').forEach(el => {
        el.remove();
      });
    };

    // Run after delays to catch dynamically injected elements
    const timers = [
      setTimeout(removeBadge, 500),
      setTimeout(removeBadge, 1500),
      setTimeout(removeBadge, 3000)
    ];

    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <Router>
      <div className="App" data-testid="app">
        {/* Noise overlay */}
        <div className="noise-overlay" />
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid #262626',
              color: '#EDEDED',
            },
          }}
        />

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>

        <IntroOverlay />
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
